import React, { useEffect, useMemo, useState } from "react";
import { Plus, User, Calendar, Hash, Archive, Trash2, Pencil, LayoutGrid, List, Search, X, Filter, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import ClientDialog from "@/components/ClientDialog";
import { TraegerDot, useTraegerStatusMap, isTraegerMissing } from "@/components/TraegerStatusCard";
import GermanDateInput from "@/components/GermanDateInput";

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthIso = (offset = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Overlap check: two [aStart, aEnd] and [bStart, bEnd] date-strings overlap
// if aStart <= bEnd && bStart <= aEnd.
const overlaps = (aStart, aEnd, bStart, bEnd) => {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart <= bEnd && bStart <= aEnd;
};

export default function AvgsPage() {
  const { user } = useAuth();
  const isAdmin = (user?.all_roles || [user?.role]).includes("admin") || !!user?.is_master_admin;
  const canManageClients = isAdmin || user?.role === "manager" || (user?.all_roles || []).includes("manager");
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // ---- Filter state ----
  const [traegerMissing, setTraegerMissing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active"); // all | upcoming | active | completed | archived
  const [monthFilter, setMonthFilter] = useState("");         // YYYY-MM (running during this month)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [view, setView] = useState(() => localStorage.getItem("avgs_view") || "cards"); // cards | list
  const [issuesByClient, setIssuesByClient] = useState({});
  const [paceByClient, setPaceByClient] = useState({});

  useEffect(() => { localStorage.setItem("avgs_view", view); }, [view]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cl, co] = await Promise.all([
        api.get("/clients"),
        api.get("/coaches"),
      ]);
      setClients(cl.data);
      setCoaches(co.data.filter((c) => c.role !== "admin"));
      setAllStaff(co.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
    api.get("/clients/pace-badges").then((r) => setPaceByClient(r.data || {})).catch(() => {});
  };

  useEffect(() => { fetchAll(); }, []);
  const traegerMap = useTraegerStatusMap("client", canManageClients);

  useEffect(() => {
    if (!canManageClients) return;
    api.get("/weekly-control/overview")
      .then(({ data }) => {
        const map = {};
        for (const c of data.clients || []) map[c.client_id] = c.issues;
        setIssuesByClient(map);
      })
      .catch(() => {});
  }, [canManageClients]);

  const remove = async (c) => {
    if (!window.confirm(`Kunde "${c.name}" wirklich löschen? Alle Anwesenheiten, Dokumentationen und der Bericht werden ebenfalls entfernt.`)) return;
    try {
      await api.delete(`/clients/${c.client_id}`);
      setClients((arr) => arr.filter((x) => x.client_id !== c.client_id));
      toast.success("Kunde gelöscht");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const coachNames = (ids = []) =>
    ids.map((id) => coaches.find((c) => c.user_id === id)?.name || "?").join(", ");

  const statusOf = (c) => {
    const today = todayIso();
    if (c.archived) return "archived";
    if (c.end_date && c.end_date < today) return "completed";
    if (c.start_date && c.start_date > today) return "upcoming";
    return "active";
  };

  // ---- Filtering ----
  const filtered = useMemo(() => {
    const today = todayIso();
    const s = search.trim().toLowerCase();
    return clients.filter((c) => {
      // Status
      const st = statusOf(c);
      if (statusFilter !== "all" && st !== statusFilter) return false;
      if (traegerMissing && !isTraegerMissing(traegerMap[c.client_id])) return false;
      // Month (running during this YYYY-MM window)
      if (monthFilter) {
        const mStart = `${monthFilter}-01`;
        const [y, m] = monthFilter.split("-").map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        const mEnd = `${monthFilter}-${String(lastDay).padStart(2, "0")}`;
        if (!overlaps(c.start_date, c.end_date, mStart, mEnd)) return false;
      }
      // Manual range (any overlap)
      if (dateFrom && dateTo) {
        if (!overlaps(c.start_date, c.end_date, dateFrom, dateTo)) return false;
      } else if (dateFrom) {
        if ((c.end_date || today) < dateFrom) return false;
      } else if (dateTo) {
        if ((c.start_date || today) > dateTo) return false;
      }
      // Search
      if (s) {
        const hay = `${c.name || ""} ${c.customer_number || ""} ${c.measure_number || ""} ${c.email || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [clients, statusFilter, monthFilter, dateFrom, dateTo, search, traegerMissing, traegerMap]);
  const traegerMissingCount = useMemo(() => clients.filter((c) => isTraegerMissing(traegerMap[c.client_id])).length, [clients, traegerMap]);

  const stats = useMemo(() => {
    const s = { all: clients.length, active: 0, upcoming: 0, completed: 0, archived: 0 };
    for (const c of clients) s[statusOf(c)]++;
    return s;
  }, [clients]);

  const resetFilters = () => {
    setStatusFilter("all"); setMonthFilter(""); setDateFrom(""); setDateTo(""); setSearch(""); setTraegerMissing(false);
  };
  const hasCustomFilter = statusFilter !== "all" || monthFilter || dateFrom || dateTo || search || traegerMissing;

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1400px] mx-auto" data-testid="avgs-root">
      <div className="flex items-end justify-between mb-8 border-b border-slate-200 pb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">AVGS</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">
            Kunden
          </h1>
          <p className="text-sm text-slate-500 mt-3 max-w-2xl">
            Verwalte AVGS-Teilnehmer/-innen, tägliche Anwesenheiten mit Unterschriften, KI-gestützte
            Coaching-Dokumentation und teilnehmerbezogene Abschlussberichte.
          </p>
        </div>
        {canManageClients && (
          <button data-testid="add-client-button" onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:bg-slate-800 transition-colors flex items-center gap-2">
            <Plus size={16} strokeWidth={1.5} /> Kunde anlegen
          </button>
        )}
      </div>

      {/* -------- Filter Bar -------- */}
      <div className="mb-6 space-y-3" data-testid="avgs-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")}
            testid="filter-all">Alle <span className="opacity-60 ml-1">{stats.all}</span></FilterChip>
          <FilterChip active={statusFilter === "active"} onClick={() => setStatusFilter("active")}
            testid="filter-active" tone="emerald">Aktiv <span className="opacity-70 ml-1">{stats.active}</span></FilterChip>
          <FilterChip active={statusFilter === "upcoming"} onClick={() => setStatusFilter("upcoming")}
            testid="filter-upcoming" tone="amber">Bald startend <span className="opacity-70 ml-1">{stats.upcoming}</span></FilterChip>
          <FilterChip active={statusFilter === "completed"} onClick={() => setStatusFilter("completed")}
            testid="filter-completed" tone="slate">Abgeschlossen <span className="opacity-70 ml-1">{stats.completed}</span></FilterChip>
          {stats.archived > 0 && (
            <FilterChip active={statusFilter === "archived"} onClick={() => setStatusFilter("archived")}
              testid="filter-archived" tone="slate">Archiviert <span className="opacity-70 ml-1">{stats.archived}</span></FilterChip>
          )}
          {canManageClients && (
            <FilterChip active={traegerMissing} onClick={() => setTraegerMissing(!traegerMissing)}
              testid="filter-traeger-missing" tone="amber">Trägerbestätigung fehlt <span className="opacity-70 ml-1">{traegerMissingCount}</span></FilterChip>
          )}
          <div className="ml-auto flex items-center gap-1 border border-slate-200 bg-white p-0.5">
            <button onClick={() => setView("cards")}
              className={`p-1.5 ${view === "cards" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="view-cards-btn" title="Karten">
              <LayoutGrid size={14} strokeWidth={1.5} />
            </button>
            <button onClick={() => setView("list")}
              className={`p-1.5 ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="view-list-btn" title="Liste">
              <List size={14} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap text-xs">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche: Name, Kd-Nr, Maßn.-Nr, E-Mail…"
              className="w-full border border-slate-300 bg-white pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:border-slate-900"
              data-testid="avgs-search" />
          </div>

          <label className="flex items-center gap-1.5 border border-slate-300 bg-white px-2 py-1 cursor-pointer" data-testid="month-filter-wrap">
            <Filter size={11} className="text-slate-500" strokeWidth={1.5} />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Monat</span>
            <input type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}
              className="text-xs focus:outline-none"
              data-testid="month-filter" />
            {monthFilter && (
              <button onClick={() => setMonthFilter("")} className="text-slate-400 hover:text-slate-900">
                <X size={12} strokeWidth={1.5} />
              </button>
            )}
          </label>

          <div className="flex items-center gap-1.5 border border-slate-300 bg-white px-2 py-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Von</span>
            <GermanDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="text-xs focus:outline-none" data-testid="date-from-filter" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Bis</span>
            <GermanDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="text-xs focus:outline-none" data-testid="date-to-filter" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-slate-400 hover:text-slate-900">
                <X size={12} strokeWidth={1.5} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1">
            <QuickBtn onClick={() => { setMonthFilter(monthIso(0)); setDateFrom(""); setDateTo(""); }} testid="quick-this-month">Diesen Monat</QuickBtn>
            <QuickBtn onClick={() => { setMonthFilter(monthIso(-1)); setDateFrom(""); setDateTo(""); }} testid="quick-last-month">Letzten Monat</QuickBtn>
            <QuickBtn onClick={() => {
              const now = new Date(); const yr = now.getFullYear();
              setDateFrom(`${yr}-01-01`); setDateTo(`${yr}-12-31`); setMonthFilter("");
            }} testid="quick-this-year">Dieses Jahr</QuickBtn>
          </div>

          {hasCustomFilter && (
            <button onClick={resetFilters}
              className="ml-auto text-xs text-slate-500 hover:text-slate-900 underline underline-offset-2"
              data-testid="reset-filters">Zurücksetzen</button>
          )}
        </div>

        <div className="text-[11px] text-slate-500 font-mono">
          {filtered.length} von {clients.length} Kunden
        </div>
      </div>

      {loading && <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Lädt…</div>}
      {!loading && filtered.length === 0 && clients.length > 0 && (
        <div className="border border-slate-200 bg-white p-10 text-center">
          <Filter size={28} strokeWidth={1} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600 mb-3">Keine Kunden entsprechen den Filtern.</p>
          <button onClick={resetFilters} className="text-xs text-slate-900 underline">Filter zurücksetzen</button>
        </div>
      )}
      {!loading && clients.length === 0 && (
        <div className="border border-slate-200 bg-white p-10 text-center">
          <Archive size={32} strokeWidth={1} className="text-slate-300 mx-auto mb-4" />
          <p className="text-sm text-slate-600">Noch keine AVGS-Kunden. Lege den ersten Kunden an.</p>
        </div>
      )}

      {/* -------- Cards view -------- */}
      {view === "cards" && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <ClientCard key={c.client_id} c={c} coachNames={coachNames} statusOf={statusOf}
              isAdmin={isAdmin} canManageClients={canManageClients} issues={issuesByClient[c.client_id]}
              pace={paceByClient[c.client_id]} traeger={traegerMap[c.client_id]}
              onEdit={() => { setEditing(c); setDialogOpen(true); }} onRemove={() => remove(c)} />
          ))}
        </div>
      )}

      {/* -------- List view -------- */}
      {view === "list" && filtered.length > 0 && (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Kd-Nr</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Maßn.-Nr</th>
                <th className="text-left px-4 py-2">Zeitraum</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2 hidden lg:table-cell">Coach</th>
                <th className="text-right px-4 py-2">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const st = statusOf(c);
                return (
                  <tr key={c.client_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`client-row-${c.client_id}`}>
                    <td className="px-4 py-2.5 font-medium">
                      <div className="flex items-center gap-1.5">
                      <Link to={`/avgs/${c.client_id}`} className="hover:underline flex items-center gap-1.5">
                        {c.name}
                        <IssueWarning issues={issuesByClient[c.client_id]} />
                        <PaceBadge pace={paceByClient[c.client_id]} />
                      </Link>
                      <TraegerDot kind="client" id={c.client_id} info={traegerMap[c.client_id]} />
                      </div>
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell font-mono text-xs text-slate-600">{c.customer_number || "—"}</td>
                    <td className="px-4 py-2.5 hidden md:table-cell font-mono text-xs text-slate-600">{c.measure_number || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">
                      {c.start_date ? formatDeShort(c.start_date) : "—"} – {c.end_date ? formatDeShort(c.end_date) : "—"}
                    </td>
                    <td className="px-4 py-2.5"><StatusChip st={st} /></td>
                    <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-slate-600">{coachNames(c.coach_user_ids) || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="inline-flex gap-1">
                        <Link to={`/avgs/${c.client_id}`}
                          className="px-2.5 py-1 bg-slate-900 text-white text-[11px] hover:bg-black"
                          data-testid={`open-client-${c.client_id}`}>Öffnen</Link>
                        {canManageClients && (
                        <button onClick={() => { setEditing(c); setDialogOpen(true); }}
                          className="p-1.5 border border-slate-300 hover:bg-slate-100"
                          data-testid={`edit-client-${c.client_id}`} title="Bearbeiten">
                          <Pencil size={11} strokeWidth={1.5} />
                        </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => remove(c)}
                            className="p-1.5 border border-slate-300 text-red-600 hover:bg-red-50"
                            data-testid={`delete-client-${c.client_id}`} title="Löschen">
                            <Trash2 size={11} strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ClientDialog open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}
        allCoaches={coaches} allStaff={allStaff} currentUserId={user?.user_id} isAdmin={isAdmin}
        initialData={editing} onSaved={fetchAll} />
    </div>
  );
}

// ---------------- helpers / subcomponents ----------------
function FilterChip({ active, onClick, children, testid, tone = "slate" }) {
  const activeCls = {
    slate: "bg-slate-900 text-white border-slate-900",
    emerald: "bg-emerald-600 text-white border-emerald-600",
    amber: "bg-amber-500 text-white border-amber-500",
  }[tone] || "bg-slate-900 text-white border-slate-900";
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 text-[11px] font-medium border transition-colors ${active ? activeCls : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

function QuickBtn({ onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="px-2 py-1 text-[11px] border border-slate-200 bg-white text-slate-600 hover:bg-slate-900 hover:text-white hover:border-slate-900">
      {children}
    </button>
  );
}

function StatusChip({ st }) {
  const meta = {
    active:    { l: "Aktiv",         cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
    upcoming:  { l: "Bald startend", cls: "bg-amber-50 text-amber-800 border-amber-300" },
    completed: { l: "Abgeschlossen", cls: "bg-slate-100 text-slate-700 border-slate-300" },
    archived:  { l: "Archiviert",    cls: "bg-slate-50 text-slate-500 border-slate-200 italic" },
  }[st] || { l: st, cls: "bg-slate-50 text-slate-600 border-slate-200" };
  return <span className={`inline-block px-2 py-0.5 text-[10px] border font-medium ${meta.cls}`}>{meta.l}</span>;
}

function IssueWarning({ issues }) {
  if (!issues || issues.length === 0) return null;
  const title = `Wochenkontrolle — ${issues.length} offene(r) Punkt(e): ` +
    [...new Set(issues.map((i) => i.category_label))].join(", ");
  return (
    <span title={title} data-testid="avgs-issue-warning" className="text-amber-500 shrink-0">
      <AlertTriangle size={13} strokeWidth={2} />
    </span>
  );
}

const PACE_DOT = { green: "bg-emerald-500", orange: "bg-amber-500", red: "bg-red-500" };
const PACE_TITLE = { green: "Auf Kurs", orange: "Achtung", red: "Kritisch" };
function PaceBadge({ pace }) {
  if (!pace || pace.pace_status === "gray" || !PACE_DOT[pace.pace_status]) return null;
  const title = `Coaching-Pensum: ${PACE_TITLE[pace.pace_status]} — ${pace.remaining_ue} UE fehlen noch` +
    (pace.required_ue_per_week_signed > 0 ? ` · ca. ${pace.required_ue_per_week_signed} UE/Woche nötig` : "");
  return (
    <span title={title} data-testid="avgs-pace-badge" className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${PACE_DOT[pace.pace_status]}`} />
  );
}

function ClientCard({ c, coachNames, statusOf, isAdmin, canManageClients, issues, pace, traeger, onEdit, onRemove }) {
  const st = statusOf(c);
  return (
    <div className="bg-white border border-slate-200 p-6 flex flex-col" data-testid={`client-card-${c.client_id}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center">
            <User size={16} strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="font-heading text-xl font-light tracking-tighter text-slate-900 flex items-center gap-1.5 min-w-0">
              <span className="truncate">{c.name}</span>
              <IssueWarning issues={issues} />
              <PaceBadge pace={pace} />
              <TraegerDot kind="client" id={c.client_id} info={traeger} />
            </div>
            <div className="text-xs text-slate-500 font-mono">
              {c.customer_number ? `Kd-Nr ${c.customer_number}` : "—"}
            </div>
          </div>
        </div>
        <StatusChip st={st} />
      </div>
      <div className="space-y-2 text-xs font-mono text-slate-700 mb-4">
        <div className="flex items-center gap-2"><Calendar size={11} strokeWidth={1.5} className="text-slate-400" />{formatDeShort(c.start_date)} – {formatDeShort(c.end_date)}</div>
        <div className="flex items-center gap-2"><Hash size={11} strokeWidth={1.5} className="text-slate-400" />Maßn. {c.measure_number || "—"}</div>
        <div className="text-slate-600">Coach: {coachNames(c.coach_user_ids) || "—"}</div>
      </div>
      <div className="mt-auto pt-4 border-t border-slate-100 flex gap-2 flex-wrap">
        <Link to={`/avgs/${c.client_id}`} data-testid={`open-client-${c.client_id}`}
          className="px-3 py-2 bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition-colors">Öffnen</Link>
        {canManageClients && (
        <button onClick={onEdit} data-testid={`edit-client-${c.client_id}`}
          className="px-3 py-2 border border-slate-200 text-xs text-slate-700 hover:bg-slate-100 flex items-center gap-1">
          <Pencil size={11} strokeWidth={1.5} /> Bearbeiten
        </button>
        )}
        {isAdmin && (
          <button onClick={onRemove} data-testid={`delete-client-${c.client_id}`}
            className="px-3 py-2 border border-slate-200 text-xs text-red-600 hover:bg-red-50 flex items-center gap-1">
            <Trash2 size={11} strokeWidth={1.5} /> Löschen
          </button>
        )}
      </div>
    </div>
  );
}
